import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Ship, Plane, ArrowLeft, Clock, MapPin, Package, Calendar, 
  DollarSign, Eye, FileText, CheckCircle, XCircle, Building2,
  AlertTriangle, Anchor, ChevronRight, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const vesselTypes = {
  container: 'Container Ship',
  bulk_carrier: 'Bulk Carrier',
  tanker: 'Tanker',
  roro: 'RoRo',
  tug: 'Tug',
  barge: 'Barge',
  general_cargo: 'General Cargo',
  reefer: 'Reefer',
  chemical_tanker: 'Chemical Tanker',
  oil_tanker: 'Oil Tanker',
  lpg_carrier: 'LPG Carrier',
  lng_carrier: 'LNG Carrier',
  car_carrier: 'Car Carrier',
  heavy_lift: 'Heavy Lift',
  offshore_supply: 'Offshore Supply',
  multipurpose: 'Multipurpose',
  passenger_ferry: 'Passenger/Cargo Ferry',
  other: 'Other'
};

const charterTermsLabels = {
  voyage: 'Voyage Charter',
  time: 'Time Charter',
  bareboat: 'Bareboat Charter',
  spot: 'Spot Charter',
  coa: 'Contract of Affreightment'
};

const CharterRFQDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchRFQDetails();
  }, [id]);

  useEffect(() => {
    if (rfq && isOwner && token) {
      fetchQuotes();
    }
  }, [rfq, isOwner, token]);

  const fetchRFQDetails = async () => {
    try {
      const res = await fetch(`${API}/api/charter-rfqs/${id}`);
      if (res.ok) {
        const data = await res.json();
        setRfq(data);
        
        // Check if current user is the owner
        if (token) {
          try {
            const userRes = await fetch(`${API}/api/users/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (userRes.ok) {
              const userData = await userRes.json();
              setIsOwner(userData.company_id === data.company_id);
            }
          } catch (e) {
            console.error('Error checking ownership:', e);
          }
        }
      } else {
        toast.error('Charter request not found');
        navigate('/charters');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load charter request');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const res = await fetch(`${API}/api/charter-quotes/rfq/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      const res = await fetch(`${API}/api/charter-quotes/${quoteId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Quote accepted successfully!');
        fetchQuotes();
        fetchRFQDetails();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to accept quote');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleDeclineQuote = async (quoteId) => {
    try {
      const res = await fetch(`${API}/api/charter-quotes/${quoteId}/decline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Quote declined');
        fetchQuotes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to decline quote');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      awarded: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.toUpperCase()}</Badge>;
  };

  const getQuoteStatusBadge = (status) => {
    const styles = {
      sent: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  if (!rfq) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="text-xl font-semibold mb-2">Charter Request Not Found</h2>
            <p className="text-slate-500 mb-4">This charter request may have been removed or is unavailable.</p>
            <Button onClick={() => navigate('/charters')}>Back to Charters</Button>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Navigation */}
          <Button
            variant="ghost"
            onClick={() => navigate('/charters')}
            className="mb-4"
            data-testid="back-to-charters"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Charters
          </Button>

          {/* Header Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4 mb-8">
            <div className="flex items-start gap-4">
              <div className={`p-4 rounded-xl ${rfq.charter_type === 'vessel' ? 'bg-cyan-100' : 'bg-purple-100'}`}>
                {rfq.charter_type === 'vessel' ? (
                  <Ship className="h-8 w-8 text-cyan-600" />
                ) : (
                  <Plane className="h-8 w-8 text-purple-600" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-slate-900" data-testid="charter-title">{rfq.title}</h1>
                  {getStatusBadge(rfq.status)}
                </div>
                <div className="flex items-center gap-4 text-slate-600">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {rfq.company_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {rfq.views_count} views
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    {rfq.quotes_count} quotes
                  </span>
                </div>
              </div>
            </div>

            {token && rfq.status === 'open' && !isOwner && (
              <Button
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={() => navigate(`/charters/${id}/quote`)}
                data-testid="submit-quote-btn"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Submit Quote
              </Button>
            )}
          </div>

          {/* Tabs for Owner View */}
          {isOwner ? (
            <Tabs defaultValue="details" className="space-y-6">
              <TabsList>
                <TabsTrigger value="details">Request Details</TabsTrigger>
                <TabsTrigger value="quotes">Quotes Received ({quotes.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <RFQDetailsContent rfq={rfq} />
              </TabsContent>

              <TabsContent value="quotes">
                {loadingQuotes ? (
                  <div className="text-center py-8">Loading quotes...</div>
                ) : quotes.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Quotes Yet</h3>
                      <p className="text-slate-500">Vessel owners and brokers can submit quotes for your charter request.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {quotes.map((quote) => (
                      <Card key={quote.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-lg">{quote.vessel_name || quote.aircraft_type}</h3>
                                {getQuoteStatusBadge(quote.status)}
                              </div>
                              <p className="text-slate-600">{quote.company_name}</p>
                              <Badge variant="outline" className="mt-1">{quote.owner_type}</Badge>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-cyan-600">
                                ${quote.freight_rate?.toLocaleString()}
                              </p>
                              <p className="text-sm text-slate-500">{quote.rate_unit}</p>
                            </div>
                          </div>

                          {quote.vessel_name && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                              <div>
                                <span className="text-slate-500">IMO:</span>
                                <p className="font-medium">{quote.vessel_imo}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">Type:</span>
                                <p className="font-medium">{vesselTypes[quote.vessel_type] || quote.vessel_type}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">DWT:</span>
                                <p className="font-medium">{quote.vessel_dwt?.toLocaleString()}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">Year Built:</span>
                                <p className="font-medium">{quote.vessel_year_built}</p>
                              </div>
                            </div>
                          )}

                          {quote.vessel_photos && quote.vessel_photos.length > 0 && (
                            <div className="flex gap-2 mb-4">
                              {quote.vessel_photos.slice(0, 3).map((photo, idx) => (
                                <img 
                                  key={idx} 
                                  src={photo} 
                                  alt={`Vessel ${idx + 1}`}
                                  className="w-24 h-16 object-cover rounded"
                                />
                              ))}
                            </div>
                          )}

                          {quote.notes && (
                            <p className="text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded">{quote.notes}</p>
                          )}

                          {quote.status === 'sent' && (
                            <div className="flex gap-3 pt-4 border-t">
                              <Button
                                onClick={() => handleAcceptQuote(quote.id)}
                                className="bg-green-500 hover:bg-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Accept Quote
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleDeclineQuote(quote.id)}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Decline
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <RFQDetailsContent rfq={rfq} />
          )}
        </div>
      </div>
    </>
  );
};

// Separated content component for RFQ details
const RFQDetailsContent = ({ rfq }) => (
  <div className="grid lg:grid-cols-3 gap-6">
    {/* Main Details */}
    <div className="lg:col-span-2 space-y-6">
      {/* Route Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Route
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Origin</p>
              <p className="font-semibold text-lg">{rfq.origin_port}</p>
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
            <div className="flex-1 text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Destination</p>
              <p className="font-semibold text-lg">{rfq.destination_port}</p>
            </div>
          </div>
          {(rfq.laycan_start || rfq.laycan_end) && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Laycan Period
              </p>
              <p className="text-amber-700 mt-1">
                {rfq.laycan_start && new Date(rfq.laycan_start).toLocaleDateString()}
                {rfq.laycan_start && rfq.laycan_end && ' - '}
                {rfq.laycan_end && new Date(rfq.laycan_end).toLocaleDateString()}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cargo Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Cargo Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-slate-500 mb-1">Description</p>
            <p className="text-slate-800">{rfq.cargo_description}</p>
          </div>
          {rfq.cargo_quantity && (
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-slate-500 mb-1">Quantity</p>
                <p className="text-lg font-semibold">{rfq.cargo_quantity.toLocaleString()} {rfq.cargo_unit}</p>
              </div>
            </div>
          )}
          {rfq.is_hazardous && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-medium text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Hazardous Cargo
              </p>
              {rfq.hazmat_class && (
                <p className="text-red-700 text-sm mt-1">Classification: {rfq.hazmat_class}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Requirements */}
      {rfq.special_requirements && (
        <Card>
          <CardHeader>
            <CardTitle>Special Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 whitespace-pre-wrap">{rfq.special_requirements}</p>
          </CardContent>
        </Card>
      )}
    </div>

    {/* Sidebar */}
    <div className="space-y-6">
      {/* Vessel Requirements */}
      {rfq.charter_type === 'vessel' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Anchor className="h-5 w-5" />
              Vessel Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {rfq.vessel_type_required && (
              <div>
                <p className="text-sm text-slate-500">Vessel Type</p>
                <p className="font-medium">{vesselTypes[rfq.vessel_type_required] || rfq.vessel_type_required}</p>
              </div>
            )}
            {rfq.charter_terms && (
              <div>
                <p className="text-sm text-slate-500">Charter Terms</p>
                <p className="font-medium">{charterTermsLabels[rfq.charter_terms] || rfq.charter_terms}</p>
              </div>
            )}
            {(rfq.vessel_size_min_dwt || rfq.vessel_size_max_dwt) && (
              <div>
                <p className="text-sm text-slate-500">DWT Range</p>
                <p className="font-medium">
                  {rfq.vessel_size_min_dwt?.toLocaleString() || 'Any'} - {rfq.vessel_size_max_dwt?.toLocaleString() || 'Any'}
                </p>
              </div>
            )}
            {rfq.max_vessel_age && (
              <div>
                <p className="text-sm text-slate-500">Max Vessel Age</p>
                <p className="font-medium">{rfq.max_vessel_age} years</p>
              </div>
            )}
            {rfq.duration_days && (
              <div>
                <p className="text-sm text-slate-500">Charter Duration</p>
                <p className="font-medium">{rfq.duration_days} days</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Budget & Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {rfq.budget_indication && (
            <div>
              <p className="text-sm text-slate-500">Budget Indication</p>
              <p className="font-medium">{rfq.budget_indication}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-slate-500">Posted</p>
            <p className="font-medium">{new Date(rfq.created_at).toLocaleDateString()}</p>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      {(rfq.contact_name || rfq.contact_email || rfq.contact_phone) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {rfq.contact_name && <p className="font-medium">{rfq.contact_name}</p>}
            {rfq.contact_email && (
              <a href={`mailto:${rfq.contact_email}`} className="text-cyan-600 hover:underline block">
                {rfq.contact_email}
              </a>
            )}
            {rfq.contact_phone && (
              <a href={`tel:${rfq.contact_phone}`} className="text-slate-600 block">
                {rfq.contact_phone}
              </a>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  </div>
);

export default CharterRFQDetail;
